
import { supabase } from "@/integrations/supabase/client";
import { AIPersonality } from "@/components/settings/AIPersonalityPanel";

type MonolithResponse = {
  response: string;
};

// Cache personalities to avoid unnecessary fetches when there's a failure
const personalityCache = new Map<string, AIPersonality>();

// Helper to get a cached personality or fetch from DB if needed
async function getPersonalityForUser(userId: string | undefined): Promise<AIPersonality> {
  if (!userId) return 'ultraterrestrial';
  
  if (personalityCache.has(userId)) {
    return personalityCache.get(userId) as AIPersonality;
  }
  
  try {
    const { data, error } = await supabase
      .from('user_settings')
      .select('ai_personality')
      .eq('user_id', userId)
      .maybeSingle();
      
    if (error || !data?.ai_personality) {
      return 'ultraterrestrial';
    }
    
    const personality = data.ai_personality as AIPersonality;
    personalityCache.set(userId, personality);
    return personality;
  } catch (error) {
    console.error('Error fetching user personality:', error);
    return 'ultraterrestrial';
  }
}

export async function sendMessageToMonolith(
  input: string, 
  personality: AIPersonality = 'ultraterrestrial',
  userName?: string,
  conversationLength: number = 0
): Promise<MonolithResponse> {
  console.log(`Sending enhanced message to Monolith with personality: ${personality}, conversation length: ${conversationLength}`);
  
  try {
    const { data, error } = await supabase.functions.invoke('openai-chat', {
      body: { 
        prompt: input,
        personality: personality,
        userName: userName,
        conversationLength: conversationLength
      }
    });

    if (error) {
      console.error(`Error from Monolith service with personality ${personality}:`, error);
      // Check for rate limit error
      if (error.message?.includes('429') || error.status === 429) {
        throw new Error('You\'ve reached your AI chat limit for this hour. Take a break and try again later!');
      }
      throw new Error(error.message);
    }
    
    console.log(`Received enhanced response from Monolith using personality: ${personality}`);
    return data as MonolithResponse;
  } catch (error) {
    console.error(`Failed to communicate with enhanced Monolith using personality ${personality}:`, error);
    throw error;
  }
}
